import { useState, useEffect } from 'react';
import { api, API_BASE } from '../lib/api.js';
import { SquareImportModal } from './SquareImportModal.jsx';
import { AddTeamMemberModal } from './AddTeamMemberModal.jsx';
import { MergeUsersModal } from './MergeUsersModal.jsx';
import { LeaveReturnsPanel } from './LeaveReturnsPanel.jsx';
import { todayStr } from '../lib/format.js';

// contact. Staff see name/role/email/locations only. Managers also see
// PIN. The full edit + PTO admin lives in the EmployeeProfile (Round 8);
// rows are clickable to open that profile when it lands. Until then the
// click is a no-op so the UX is stable through the IA shuffle.
export function TeamDirectoryView({ user, locations, onOpenProfile }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [showSquare, setShowSquare] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [mergeLoser, setMergeLoser] = useState(null); // user object being merged AWAY
  const isManager = window.EmpireRoles.isManager(user);
  const isOwner = window.EmpireRoles.isOwner(user);
  const headers = { 'x-user-id': String(user.id) };

  function loadUsers() {
    const url = showArchived ? '/api/users?include_archived=1' : '/api/users';
    return api('GET', url, null, headers)
      .then((rows) => { setUsers(Array.isArray(rows) ? rows : []); setLoading(false); })
      .catch(() => setLoading(false));
  }

  useEffect(() => { loadUsers(); }, [showArchived]);

  async function archive(u) {
    if (!confirm(`Archive ${u.name}? They'll be hidden from rosters, chat recipients, and payroll. You can restore later.`)) return;
    try {
      await api('PUT', `/api/admin/users/${u.id}/archive`, {}, headers);
      loadUsers();
    } catch (e) { alert(e.message || 'Archive failed'); }
  }
  async function restore(u) {
    try {
      await api('PUT', `/api/admin/users/${u.id}/restore`, {}, headers);
      loadUsers();
    } catch (e) { alert(e.message || 'Restore failed'); }
  }
  async function hardDelete(u) {
    if (!confirm(
      `Permanently delete ${u.name}? Their history (audit trail, chat, payroll) is kept for the record, ` +
      `but the email "${u.email}" will be freed up so it can be used for a new signup. This cannot be undone.`
    )) return;
    try {
      await api('DELETE', `/api/admin/users/${u.id}`, null, headers);
      loadUsers();
    } catch (e) { alert(e.message || 'Delete failed'); }
  }

  const q = query.trim().toLowerCase();
  const filtered = q
    ? users.filter((u) => (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q))
    : users;
  const sorted = [...filtered].sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  function locationLabels(u) {
    let ids = [];
    try { ids = JSON.parse(u.location_ids || '[]'); } catch {}
    if ((!ids || ids.length === 0) && u.location_id) ids = [u.location_id];
    if (!ids.length) return [];
    return ids.map((id) => locations.find((l) => l.id === id)?.name).filter(Boolean);
  }

  return (
    <div className="space-y-3">
      {showSquare && (
        <SquareImportModal
          currentUser={user}
          onClose={() => setShowSquare(false)}
          onImported={loadUsers} />
      )}
      {showAdd && (
        <AddTeamMemberModal currentUser={user} locations={locations}
          onClose={() => setShowAdd(false)}
          onCreated={() => { setShowAdd(false); loadUsers(); }} />
      )}
      {mergeLoser && (
        <MergeUsersModal currentUser={user} loser={mergeLoser} allUsers={users}
          onClose={() => setMergeLoser(null)}
          onMerged={() => { setMergeLoser(null); loadUsers(); }} />
      )}
      {/* Staff who have said when they are back — above the directory because
          it is the one thing on this page that is waiting on the manager.
          Renders nothing when the queue is empty. */}
      {isManager && <LeaveReturnsPanel currentUser={user} onChanged={loadUsers} />}
      <div className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div className="text-base font-semibold">Team directory</div>
          <div className="text-xs text-gray-500">
            {users.length} team members{showArchived ? ' (incl. archived)' : ''}.
            Search to find a coworker for shift coverage.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name or email"
            style={{ flex: '0 1 280px', padding: '6px 10px', fontSize: 13, border: '1px solid #d0d4d9', borderRadius: 6, minWidth: 180 }} />
          {/* CJ 2026-08-04: "managers cant see the archived employees". This
              was gated on isOwner while the Restore button beside it is
              isManager and the server route is view_admin_panel — so a
              manager was allowed to restore somebody they had no way to
              find. Not a security control either: /api/users?include_archived=1
              has no role gate, so the data was already reachable; the toggle
              was just missing. Matched to the capability it enables. */}
          {isManager && (
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#757575' }}>
              <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
              Show archived
            </label>
          )}
          {/* CJ 2026-07-29: manual add is owner-only; managers import from
              Square instead (below) so the roster stays Square-sourced. */}
          {isOwner && (
            <button onClick={() => setShowAdd(true)}
              style={{ padding: '6px 12px', fontSize: 13, fontWeight: 600, background: 'var(--forest, #1B2B1B)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              + Add team member
            </button>
          )}
          {isManager && (
            <button onClick={() => setShowSquare(true)}
              style={{ padding: '6px 12px', fontSize: 13, fontWeight: 600, background: '#16a34a', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              ⬆ Import from Square
            </button>
          )}
          {isOwner && (
            <button onClick={async () => {
              // Plain <a download> would skip the auth headers that api()
              // attaches; fetch the CSV with the same auth as every other
              // request, then trigger a Blob download client-side.
              try {
                const token = localStorage.getItem('monarchIdToken');
                const h = { 'x-user-id': String(user.id) };
                if (token) h.Authorization = `Bearer ${token}`;
                const r = await fetch(`${API_BASE || ''}/api/admin/users/export.csv`, { headers: h });
                if (!r.ok) throw new Error(await r.text() || `HTTP ${r.status}`);
                const blob = await r.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                const ymd = todayStr();
                a.href = url; a.download = `empire-team-${ymd}.csv`;
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(url);
              } catch (e) {
                let m = e?.message || 'Export failed';
                try { const p = JSON.parse(m); if (p?.error) m = p.error; } catch (_) {}
                alert(m);
              }
            }}
              title="Download the full team list (active + archived) as CSV"
              style={{ padding: '6px 12px', fontSize: 13, fontWeight: 600, background: '#fff', color: 'var(--forest, #1B2B1B)', border: '1px solid var(--forest, #1B2B1B)', borderRadius: 6, cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'inherit' }}>
              ⬇ Export CSV
            </button>
          )}
        </div>
      </div>
      {loading && <div className="card text-sm text-gray-500">Loading…</div>}
      {!loading && sorted.length === 0 && (
        <div className="card text-sm text-gray-500">No matches.</div>
      )}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ borderBottom: '1px solid #f0f0f0' }}>
          {sorted.map((u) => {
            const locs = locationLabels(u);
            const archived = (u.status || 'active') === 'archived';
            return (
              <div key={u.id}
                onClick={() => onOpenProfile && onOpenProfile(u.id)}
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderBottom: '1px solid #fafafa',
                          cursor: onOpenProfile ? 'pointer' : 'default',
                          opacity: archived ? 0.55 : 1 }}
                onMouseEnter={(e) => { if (onOpenProfile) e.currentTarget.style.background = '#f7f8f9'; }}
                onMouseLeave={(e) => { if (onOpenProfile) e.currentTarget.style.background = 'transparent'; }}>
                <div style={{ width: 34, height: 34, borderRadius: '50%', background: '#e8f5e9', color: 'var(--forest)',
                              fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {(u.name || '?').slice(0, 1).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#212121' }}>
                    {u.name}
                    {archived && <span style={{ fontSize: 10, marginLeft: 6, color: '#9e9e9e' }}>archived</span>}
                  </div>
                  <div style={{ fontSize: 11, color: '#757575' }}>{u.email || '—'}</div>
                  {locs.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 3 }}>
                      {locs.map((n) => (
                        <span key={n} style={{ fontSize: 10, padding: '1px 6px', background: '#f5f5f5', color: '#37474f', borderRadius: 4 }}>{n}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                  <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 999, fontWeight: 600,
                                background: ((u.role || '').toLowerCase() === 'owner') ? '#fff3e0'
                                          : ((u.role || '').toLowerCase() === 'manager') ? '#e3f2fd'
                                          : '#f5f5f5',
                                color: ((u.role || '').toLowerCase() === 'owner') ? '#e65100'
                                     : ((u.role || '').toLowerCase() === 'manager') ? '#1565c0'
                                     : '#37474f' }}>
                    {u.role || '—'}
                  </span>
                  {isManager && u.pin && (
                    <span style={{ fontSize: 10, color: '#9e9e9e' }}>PIN: {u.pin}</span>
                  )}
                  {isManager && u.id !== user.id && (
                    archived ? (
                      <>
                        {/* CJ 2026-08-04: this was 10px green-on-white with a
                            #bbf7d0 border and read as greyed out — he assumed
                            it was disabled. It never was. On an archived row
                            Restore is the primary thing you'd want to do, so
                            it now looks like one. */}
                        <button onClick={(e) => { e.stopPropagation(); restore(u); }}
                          title="Re-activate this person"
                          style={{ fontSize: 12, fontWeight: 600, padding: '5px 12px',
                                    background: 'var(--forest)', color: '#fff',
                                    border: 'none', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit' }}>
                          Restore
                        </button>
                        {isOwner && (
                          <button onClick={(e) => { e.stopPropagation(); hardDelete(u); }}
                            style={{ fontSize: 10, padding: '2px 8px', background: '#fff', color: '#991b1b',
                                      border: '1px solid #fecaca', borderRadius: 4, cursor: 'pointer', fontFamily: 'inherit',
                                      marginLeft: 4 }}>
                            Delete
                          </button>
                        )}
                      </>
                    ) : (
                      <div style={{ display: 'flex', gap: 4 }}>
                        {isOwner && (
                          <button onClick={(e) => { e.stopPropagation(); setMergeLoser(u); }}
                            title="Merge this user into another (rewrites all history to the keeper, archives this row)"
                            style={{ fontSize: 10, padding: '2px 8px', background: '#fff', color: '#5b21b6',
                                      border: '1px solid #ddd6fe', borderRadius: 4, cursor: 'pointer', fontFamily: 'inherit' }}>
                            Merge…
                          </button>
                        )}
                        <button onClick={(e) => { e.stopPropagation(); archive(u); }}
                          style={{ fontSize: 10, padding: '2px 8px', background: '#fff', color: '#dc2626',
                                    border: '1px solid #fecaca', borderRadius: 4, cursor: 'pointer', fontFamily: 'inherit' }}>
                          Archive
                        </button>
                      </div>
                    )
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="text-xs text-gray-400 px-1">
        Click a row to open the full profile.
      </div>
    </div>
  );
}
